import i18n from '@/i18n'
import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { Database } from '@/lib/supabase'

type Account = Database['public']['Tables']['accounts']['Row']

interface UseAgencyAccountsReturn {
  accounts: Account[]
  loading: boolean
  error: string | null
  currentAccount: Account | null
  isAgencyUser: boolean
  hasMultipleAccounts: boolean
  switchAccount: (accountId: string) => Promise<boolean>
  refetch: () => Promise<void>
}

export const useAgencyAccounts = (): UseAgencyAccountsReturn => {
  const { user } = useAuth()
  const [accounts, setAccounts] = useState<Account[]>([])
  const [currentAccount, setCurrentAccount] = useState<Account | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isAgencyUser, setIsAgencyUser] = useState(false)
  const [hasMultipleAccounts, setHasMultipleAccounts] = useState(false)

  const checkIfAgencyUser = async (): Promise<{ isAgency: boolean; agencyId?: string }> => {
    if (!user || !supabase) {
      return { isAgency: false }
    }

    // Verificar se o usuário tem auth_user_id
    if (!user.auth_user_id) {
      console.log('⚠️ [useAgencyAccounts] Usuário sem auth_user_id')
      return { isAgency: false }
    }

    // Obter agency_id da conta atual
    const currentAccountAgencyId = user.account?.agency_id

    if (!currentAccountAgencyId) {
      console.log('⚠️ [useAgencyAccounts] Usuário sem agency_id na conta atual')
      return { isAgency: false }
    }

    try {
      console.log('🔍 [useAgencyAccounts] Verificando se usuário está em agency_users:', {
        auth_user_id: user.auth_user_id,
        agency_id: currentAccountAgencyId
      })

      // Buscar por auth_user_id E agency_id (mesma lógica do login)
      const { data: agencyUserData, error: agencyUserError } = await supabase
        .from('agency_users')
        .select('id, email, name, agency_id, auth_user_id')
        .eq('auth_user_id', user.auth_user_id)
        .eq('agency_id', currentAccountAgencyId)
        .maybeSingle()

      if (agencyUserError) {
        console.error('❌ [useAgencyAccounts] Erro ao verificar agency_user:', agencyUserError)
        return { isAgency: false }
      }

      const isAgency = !!agencyUserData

      console.log(isAgency ? '✅' : 'ℹ️', '[useAgencyAccounts] Usuário', isAgency ? 'encontrado' : 'não encontrado', 'em agency_users:', {
        auth_user_id: user.auth_user_id,
        agency_id: currentAccountAgencyId,
        isAgencyUser: isAgency,
        agencyUserId: agencyUserData?.id
      })

      setIsAgencyUser(isAgency)
      return { isAgency, agencyId: agencyUserData?.agency_id }

    } catch (err) {
      console.error('❌ [useAgencyAccounts] Erro ao verificar agency_user:', err)
      return { isAgency: false }
    }
  }

  const checkIfMultiAccountUser = (): {
    hasMultipleAccounts: boolean
    accounts: Account[]
    isAccountUser: boolean
  } => {
    // Verificar se é account_user
    if (user?.type !== 'account_user') {
      return { hasMultipleAccounts: false, accounts: [], isAccountUser: false }
    }

    // Buscar contas disponíveis do localStorage
    const sessionData = localStorage.getItem('rolt_user_session')
    if (!sessionData) {
      return { hasMultipleAccounts: false, accounts: [], isAccountUser: true }
    }

    try {
      const session = JSON.parse(sessionData)
      const availableAccounts = session.availableAccounts || []

      console.log('🔍 [useAgencyAccounts] Verificando account_user multi-conta:', {
        user_type: user.type,
        available_accounts: availableAccounts.length,
        current_account: user.account_id
      })

      return {
        hasMultipleAccounts: availableAccounts.length > 1,
        accounts: availableAccounts,
        isAccountUser: true
      }
    } catch (err) {
      console.error('❌ [useAgencyAccounts] Erro ao verificar multi-conta:', err)
      return { hasMultipleAccounts: false, accounts: [], isAccountUser: true }
    }
  }

  const fetchAccounts = async (): Promise<void> => {
    if (!user || !supabase) {
      console.log('⚠️ [useAgencyAccounts] Sem usuário ou supabase')
      setAccounts([])
      setIsAgencyUser(false)
      setLoading(false)
      return
    }

    // PRIORIDADE 1: Verificar se é agency_user
    const { isAgency } = await checkIfAgencyUser()

    console.log('🔍 [useAgencyAccounts] Resultado checkIfAgencyUser:', { isAgency })

    if (isAgency) {
      // Lógica para agency_users continua abaixo
      console.log('✅ [useAgencyAccounts] Processando como agency_user')
    } else {
      // PRIORIDADE 2: Verificar se é account_user com múltiplas contas
      const multiAccountCheck = checkIfMultiAccountUser()

      console.log('🔍 [useAgencyAccounts] Resultado checkIfMultiAccountUser:', {
        isAccountUser: multiAccountCheck.isAccountUser,
        hasMultiple: multiAccountCheck.hasMultipleAccounts,
        total: multiAccountCheck.accounts.length
      })

      if (multiAccountCheck.isAccountUser && multiAccountCheck.hasMultipleAccounts) {
        console.log('✅ [useAgencyAccounts] account_user com múltiplas contas:', {
          total: multiAccountCheck.accounts.length,
          current: user.account_id,
          accounts: multiAccountCheck.accounts.map(acc => ({ id: acc.id, nome: acc.nome }))
        })

        const activeAccounts = multiAccountCheck.accounts.filter(
          (acc: Account) => (acc as any).is_active !== false
        )

        setAccounts(activeAccounts)
        setIsAgencyUser(false)
        setHasMultipleAccounts(activeAccounts.length > 1)

        const current = activeAccounts.find(acc => acc.id === user.account_id)
        setCurrentAccount(current || null)

        setLoading(false)
        return
      }

      // Caso padrão: usuário não tem múltiplas contas
      console.log('ℹ️ [useAgencyAccounts] Usuário não é agency_user nem tem múltiplas contas')
      setAccounts([])
      setIsAgencyUser(false)
      setLoading(false)
      return
    }

    // Obter agency_id da conta atual do usuário logado
    const currentAccountAgencyId = user.account?.agency_id

    if (!currentAccountAgencyId) {
      console.error('❌ [useAgencyAccounts] Conta atual sem agency_id:', {
        user_id: user.id,
        account_id: user.account_id,
        has_account: !!user.account
      })
      setAccounts([])
      setIsAgencyUser(true)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      console.log('🔍 [useAgencyAccounts] Buscando contas da agência:', {
        user_id: user.id,
        user_email: user.email,
        current_account_id: user.account_id,
        agency_id: currentAccountAgencyId,
        is_agency_user: true
      })

      // Buscar apenas contas ativas da mesma agência da conta atual do usuário
      const { data: accountsData, error: accountsError } = await supabase
        .from('accounts')
        .select('*')
        .eq('agency_id', currentAccountAgencyId)
        .eq('is_active', true)
        .order('nome', { ascending: true })

      if (accountsError) {
        console.error('❌ [useAgencyAccounts] Erro ao buscar contas:', accountsError)
        throw accountsError
      }

      console.log('✅ [useAgencyAccounts] Contas da agência encontradas:', {
        agency_id: currentAccountAgencyId,
        total: accountsData?.length || 0,
        accounts: accountsData?.map(acc => ({ id: acc.id, nome: acc.nome }))
      })

      setAccounts(accountsData || [])

      // Definir conta atual
      const current = accountsData?.find(acc => acc.id === user.account_id) || null
      setCurrentAccount(current)

      console.log('📍 [useAgencyAccounts] Conta atual:', {
        id: current?.id,
        nome: current?.nome
      })

    } catch (err: any) {
      console.error('❌ [useAgencyAccounts] Erro ao buscar contas:', err)
      setError(err.message || 'Erro ao carregar contas')
    } finally {
      setLoading(false)
    }
  }

  const switchAccountForAccountUser = async (accountId: string): Promise<boolean> => {
    if (!user || !supabase) {
      console.error('❌ [useAgencyAccounts] Usuário não autenticado')
      return false
    }

    if (user.type !== 'account_user') {
      console.error('❌ [useAgencyAccounts] Função apenas para account_users')
      return false
    }

    try {
      setLoading(true)
      setError(null)

      console.log('🔄 [useAgencyAccounts] account_user trocando conta:', {
        user_type: user.type,
        from: user.account_id,
        to: accountId
      })

      // Buscar nova conta na lista de contas disponíveis
      const newAccount = accounts.find(acc => acc.id === accountId)

      if (!newAccount) {
        throw new Error(i18n.t('agency:errors.accountNotFound'))
      }

      // Validar que a conta pertence à mesma agência
      if (newAccount.agency_id !== user.account?.agency_id) {
        throw new Error(i18n.t('agency:errors.accountNotInAgency'))
      }

      console.log('✅ [useAgencyAccounts] Nova conta encontrada e validada:', {
        id: newAccount.id,
        nome: newAccount.nome,
        agency_id: newAccount.agency_id
      })

      // Buscar dados atualizados do account_user na nova conta
      const { data: newAccountUserData, error: fetchError } = await supabase
        .from('account_users')
        .select(`
          id,
          email,
          name,
          phone,
          profile,
          account_id,
          role,
          view_all_contacts,
          can_assign_contacts,
          view_unassigned_contacts,
          is_active
        `)
        .eq('auth_user_id', user.auth_user_id)
        .eq('account_id', accountId)
        .maybeSingle()

      if (fetchError || !newAccountUserData) {
        console.error('❌ [useAgencyAccounts] Erro ao buscar dados do usuário na nova conta:', fetchError)
        throw new Error(i18n.t('agency:errors.fetchUserDataError'))
      }

      console.log('✅ [useAgencyAccounts] Dados do usuário na nova conta encontrados:', {
        id: newAccountUserData.id,
        email: newAccountUserData.email,
        is_active: newAccountUserData.is_active
      })

      // Validar que usuário está ativo na nova conta
      if (!newAccountUserData.is_active) {
        throw new Error(i18n.t('agency:errors.userInactive'))
      }

      // Marcar conta como ativa no banco (atualiza last_login)
      const { error: rpcError } = await supabase.rpc('set_active_account', {
        p_account_id: accountId
      })

      if (rpcError) {
        console.error('❌ [useAgencyAccounts] Erro ao marcar conta ativa no banco:', rpcError)
      }

      // Atualizar sessão no localStorage
      const sessionData = localStorage.getItem('rolt_user_session')
      if (!sessionData) {
        throw new Error(i18n.t('common:errors.sessionNotFound'))
      }

      const session = JSON.parse(sessionData)

      // IMPORTANTE: Atualizar com dados do account_user da NOVA conta
      const updatedUser = {
        ...newAccountUserData,
        account_id: newAccount.id,
        account: newAccount,
        auth_user_id: user.auth_user_id,
        type: 'account_user' as const
      }

      const updatedSession = {
        ...session,
        user: updatedUser
      }

      localStorage.setItem('rolt_user_session', JSON.stringify(updatedSession))

      console.log('✅ [useAgencyAccounts] Sessão atualizada com dados da nova conta:', {
        old_account: user.account_id,
        new_account: newAccount.id,
        new_account_name: newAccount.nome,
        user_id: newAccountUserData.id
      })

      // Recarregar página para aplicar mudanças
      console.log('🔄 [useAgencyAccounts] Recarregando página...')
      window.location.reload()

      return true

    } catch (err: any) {
      console.error('❌ [useAgencyAccounts] Erro ao trocar conta:', err)
      setError(err.message || 'Erro ao trocar de conta')
      return false
    } finally {
      setLoading(false)
    }
  }

  const switchAccountForAgencyUser = async (accountId: string): Promise<boolean> => {
    if (!user || !supabase) {
      console.error('❌ [useAgencyAccounts] Usuário não autenticado')
      return false
    }

    // Verificar se o usuário é agency_user
    const { isAgency } = await checkIfAgencyUser()

    if (!isAgency) {
      console.error('❌ [useAgencyAccounts] Usuário não está em agency_users', {
        auth_user_id: user.auth_user_id,
        agency_id: user.account?.agency_id
      })
      setError(i18n.t('agency:errors.onlyAgencyUsers'))
      return false
    }

    // Obter agency_id da conta atual
    const currentAccountAgencyId = user.account?.agency_id

    if (!currentAccountAgencyId) {
      console.error('❌ [useAgencyAccounts] Conta atual sem agency_id')
      setError(i18n.t('agency:errors.cannotDetermineAgency'))
      return false
    }

    try {
      setLoading(true)
      setError(null)

      console.log('🔄 [useAgencyAccounts] Iniciando switch de conta:', {
        from_account: user.account_id,
        to_account: accountId,
        agency_id: currentAccountAgencyId
      })

      // Buscar a nova conta
      const newAccount = accounts.find(acc => acc.id === accountId)

      if (!newAccount) {
        throw new Error(i18n.t('agency:errors.accountNotFound'))
      }

      // Validar se a conta pertence à mesma agência
      if (newAccount.agency_id !== currentAccountAgencyId) {
        console.error('❌ [useAgencyAccounts] Conta não pertence à agência do usuário:', {
          account_agency_id: newAccount.agency_id,
          current_agency_id: currentAccountAgencyId
        })
        throw new Error(i18n.t('agency:errors.onlyYourAgencyAccounts'))
      }

      console.log('✅ [useAgencyAccounts] Nova conta encontrada e validada:', {
        id: newAccount.id,
        nome: newAccount.nome,
        agency_id: newAccount.agency_id
      })

      // Atualizar a sessão do usuário com a nova conta
      const sessionData = localStorage.getItem('rolt_user_session')

      if (!sessionData) {
        throw new Error(i18n.t('common:errors.sessionNotFound'))
      }

      const session = JSON.parse(sessionData)

      // IMPORTANTE: Manter todos os dados do agency_user, apenas trocar account_id e account
      const updatedUser = {
        ...session.user,
        account_id: newAccount.id,
        account: newAccount
      }

      const updatedSession = {
        ...session,
        user: updatedUser
      }

      // Salvar sessão atualizada
      localStorage.setItem('rolt_user_session', JSON.stringify(updatedSession))

      console.log('💾 [useAgencyAccounts] Sessão atualizada - mantendo dados do agency_user:', {
        user_id: session.user.id,
        email: session.user.email,
        auth_user_id: session.user.auth_user_id,
        old_account_id: user.account_id,
        new_account_id: newAccount.id,
        new_account_name: newAccount.nome,
        kept_user_data: 'Todos os dados do agency_user foram mantidos'
      })

      // IMPORTANTE: Atualizar a sessão no banco para que as RLS policies funcionem
      console.log('🔐 [useAgencyAccounts] Atualizando sessão RLS no banco...')
      try {
        const { error: sessionError } = await supabase.rpc('update_agency_user_session', {
          p_account_id: newAccount.id
        })

        if (sessionError) {
          console.error('❌ [useAgencyAccounts] Erro ao atualizar sessão RLS:', sessionError)
          throw new Error(i18n.t('agency:errors.updatePermissionsError'))
        }

        console.log('✅ [useAgencyAccounts] Sessão RLS atualizada com sucesso')
      } catch (err) {
        console.error('❌ [useAgencyAccounts] Erro crítico ao atualizar sessão RLS:', err)
        throw err
      }

      // Definir nova conta atual no estado
      setCurrentAccount(newAccount)

      console.log('🔄 [useAgencyAccounts] Recarregando página para aplicar mudanças...')

      // Recarregar a página para aplicar as mudanças
      window.location.reload()

      return true

    } catch (err: any) {
      console.error('❌ [useAgencyAccounts] Erro ao trocar conta:', err)
      setError(err.message || 'Erro ao trocar de conta')
      return false
    } finally {
      setLoading(false)
    }
  }

  const switchAccount = async (accountId: string): Promise<boolean> => {
    if (!user || !supabase) {
      console.error('❌ [useAgencyAccounts] Usuário não autenticado')
      return false
    }

    console.log('🔄 [useAgencyAccounts] Detectando tipo de usuário para switch:', {
      user_type: user.type,
      user_email: user.email,
      from_account: user.account_id,
      to_account: accountId
    })

    // Detectar tipo de usuário e chamar função apropriada
    if (user.type === 'agency_user') {
      console.log('✅ [useAgencyAccounts] Direcionando para switchAccountForAgencyUser')
      return switchAccountForAgencyUser(accountId)
    } else if (user.type === 'account_user') {
      console.log('✅ [useAgencyAccounts] Direcionando para switchAccountForAccountUser')
      return switchAccountForAccountUser(accountId)
    }

    console.error('❌ [useAgencyAccounts] Tipo de usuário desconhecido:', user.type)
    setError(i18n.t('agency:errors.unsupportedUserType'))
    return false
  }

  const refetch = async (): Promise<void> => {
    await fetchAccounts()
  }

  useEffect(() => {
    if (user) {
      fetchAccounts()
    }
  }, [user?.id, user?.email])

  return {
    accounts,
    loading,
    error,
    currentAccount,
    isAgencyUser,
    hasMultipleAccounts,
    switchAccount,
    refetch
  }
}
